import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { OrganizationDetail } from '@/components/admin/organizations/organization-detail';
import { LifecycleActions } from '@/components/admin/organizations/lifecycle-actions';
import { DissolveAgencyAction } from '@/components/admin/organizations/dissolve-agency-action';
import { ResetDemoAction } from '@/components/admin/organizations/reset-demo-action';
import { SetLlmModelAction } from '@/components/admin/organizations/set-llm-model-action';
import { MaintenanceActions } from '@/components/admin/organizations/maintenance-actions';
import { BillingPanel } from '@/components/admin/organizations/billing-panel';
import { BillingActions } from '@/components/admin/organizations/billing-actions/billing-actions';
import { InvitationCreate } from '@/components/admin/invitations/invitation-create/invitation-create';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminOrganizationDetail } from '@/hooks/data/admin/use-organizations';
import { useAdminOrganizationBilling } from '@/hooks/data/admin/use-billing';
import { ROUTES } from '@/constants/routes';
import { loadNamespaces } from '@/lib/i18n/i18n';

const paramSchema = z.object({ orgId: z.coerce.number().int().positive() });

export const Route = createFileRoute(
  '/_authenticated/admin/organizations/$orgId',
)({
  loader: () => loadNamespaces('settings'),
  staticData: { title: 'Organization' },
  parseParams: (params) => paramSchema.parse(params),
  component: AdminOrganizationDetailPage,
});

function DetailSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-4 px-4 py-6 sm:px-8 @3xl:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton variant="card" key={i} className="h-48 rounded-xl" />
      ))}
    </div>
  );
}

function BillingSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-4 px-4 py-6 sm:px-8 @3xl:grid-cols-2">
      {Array.from({ length: 2 }).map((_, i) => (
        <Skeleton variant="card" key={i} className="h-40 rounded-xl" />
      ))}
    </div>
  );
}

function AdminOrganizationDetailPage(): React.ReactElement {
  const { orgId } = Route.useParams();
  const detailQuery = useAdminOrganizationDetail(orgId);
  const billingQuery = useAdminOrganizationBilling(orgId);
  const [inviteOpen, setInviteOpen] = useState(false);

  if (detailQuery.isLoading) {
    return (
      <main>
        <AdminPageHeader title="Organization" />
        <DetailSkeleton />
      </main>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <main>
        <AdminPageHeader
          title="Organization"
          actions={
            <Button variant="outline" asChild>
              <Link to={ROUTES.ADMIN_ORGANIZATIONS}>Back</Link>
            </Button>
          }
        />
        <div className="px-4 py-12 text-center sm:px-8">
          <p className="text-fg-3 text-fs-13">
            Failed to load organization. Please try again.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main>
      <AdminPageHeader
        title={detailQuery.data.name}
        description={`Organization #${detailQuery.data.id}`}
        actions={
          <>
            <Button variant="outline" onClick={() => setInviteOpen(true)}>
              Invite user
            </Button>
            <ResetDemoAction org={detailQuery.data} />
            <SetLlmModelAction org={detailQuery.data} />
            <DissolveAgencyAction org={detailQuery.data} />
            <LifecycleActions org={detailQuery.data} />
          </>
        }
      />
      <InvitationCreate
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        defaultOrgId={orgId}
        lockOrg
      />
      <OrganizationDetail data={detailQuery.data} />
      {billingQuery.isLoading && <BillingSkeleton />}
      {billingQuery.isError && (
        <div className="px-4 py-6 sm:px-8">
          <p className="text-fg-3 text-fs-13">
            Failed to load billing. Please try again.
          </p>
        </div>
      )}
      {billingQuery.data && (
        <>
          <BillingPanel org={detailQuery.data} billing={billingQuery.data} />
          <BillingActions orgId={orgId} billing={billingQuery.data} />
        </>
      )}
      <MaintenanceActions org={detailQuery.data} />
    </main>
  );
}
